import { useComputedColorScheme } from "@mantine/core";
import { useMemo } from "react";

import { EChart, type EChartOption } from "@/viz/EChart";
import { VIZ_FONT, vizTokens } from "@/viz/tokens";

interface Props {
  keys: string[];
  values: (number | null)[];
  /** Names the measure, for the tooltip. */
  valueLabel: string;
  formatValue?: (v: number) => string;
}

/**
 * Part-to-whole across a handful of categories.
 *
 * A donut rather than a full pie: the hole gives the labels room and stops the
 * eye trying to compare centre angles. It is only the right form for a share
 * read *at a glance* — comparing close values is a bar chart's job, and a
 * two-slice pie is a stat tile.
 *
 * Slices carry percentages; the legend carries identity. That split is
 * deliberate — names on both would be redundant, and identity must never rest
 * on color-matching alone.
 */

/** Six categorical slots exist, so the sixth slice is "Other" when folding. */
const MAX_SLICES = 6;
const PLOT_HEIGHT = 240;
const LEGEND_HEIGHT = 56;

export function PieChart({ keys, values, valueLabel, formatValue }: Props) {
  const scheme = useComputedColorScheme("light", { getInitialValueInEffect: true });

  const slices = useMemo(() => fold(keys, values), [keys, values]);

  const option = useMemo<EChartOption>(() => {
    const t = vizTokens(scheme);
    const fmt = formatValue ?? ((v: number) => v.toLocaleString());

    return {
      backgroundColor: "transparent",
      animationDuration: 260,
      legend: {
        bottom: 0,
        left: "center",
        icon: "circle",
        itemWidth: 9,
        itemHeight: 9,
        itemGap: 16,
        // Ink, never the slice color: a light categorical hue is illegible as
        // text. The swatch beside the label carries identity.
        textStyle: { color: t.textSecondary, fontFamily: VIZ_FONT, fontSize: 12 },
      },
      tooltip: {
        trigger: "item",
        backgroundColor: t.tooltipBg,
        borderColor: t.tooltipBorder,
        borderWidth: 1,
        padding: [8, 12],
        textStyle: { color: t.textPrimary, fontFamily: VIZ_FONT, fontSize: 13 },
        extraCssText: "border-radius:8px; box-shadow:0 4px 16px rgba(0,0,0,0.12);",
        formatter: (p: { name: string; value: number; percent: number }) =>
          `<strong>${escapeHtml(p.name)}</strong><br/>` +
          `<span style="color:${t.textSecondary}">${escapeHtml(valueLabel)}:</span> ` +
          `${fmt(p.value)} (${p.percent}%)`,
      },
      series: [
        {
          type: "pie",
          // A donut, not a disc; the ring is thin rather than a heavy block.
          radius: ["48%", "72%"],
          center: ["50%", "46%"],
          avoidLabelOverlap: true,
          data: slices.map((slice, i) => ({
            name: slice.name,
            value: slice.value,
            itemStyle: {
              // "Other" is a fold, not a category, so it takes the
              // de-emphasis gray rather than a seventh generated hue.
              color: slice.isOther ? t.otherFill : t.categorical[i],
            },
          })),
          itemStyle: {
            // The 2px gap is in the surface color: white does the separating,
            // never a border drawn around the mark.
            borderColor: t.surface,
            borderWidth: 2,
          },
          label: {
            show: true,
            position: "outside",
            // Percent only. The legend already names each slice, and a name on
            // both would be noise.
            formatter: "{d}%",
            color: t.textSecondary,
            fontFamily: VIZ_FONT,
            fontSize: 12,
          },
          labelLine: {
            length: 8,
            length2: 8,
            lineStyle: { color: t.axis, width: 1 },
          },
          emphasis: {
            scaleSize: 4,
            itemStyle: { borderColor: t.surface, borderWidth: 2 },
          },
        },
      ],
    } as EChartOption;
  }, [slices, valueLabel, formatValue, scheme]);

  return (
    <EChart
      option={option}
      height={PLOT_HEIGHT + LEGEND_HEIGHT}
      ariaLabel={
        `Donut chart: ${valueLabel} split across ${slices.length} categories. ` +
        `The table view lists every value.`
      }
    />
  );
}

interface Slice {
  name: string;
  value: number;
  isOther: boolean;
}

/**
 * Keep the largest categories and fold the tail into one "Other" slice.
 *
 * Past six, more slices means either a generated hue — indistinguishable from
 * an existing one under colorblindness — or segments too thin to read. The
 * data arrives sorted by value, so the tail is genuinely the small end, and
 * the full breakdown stays available in the table view.
 */
function fold(keys: string[], values: (number | null)[]): Slice[] {
  const all = keys.map((name, i) => ({
    name,
    value: values[i] ?? 0,
    isOther: false,
  }));

  if (all.length <= MAX_SLICES) return all;

  const head = all.slice(0, MAX_SLICES - 1);
  const tail = all.slice(MAX_SLICES - 1);

  return [
    ...head,
    {
      name: `Other (${tail.length})`,
      value: tail.reduce((sum, slice) => sum + slice.value, 0),
      isOther: true,
    },
  ];
}

function escapeHtml(value: string): string {
  return value.replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!,
  );
}
